import { CafeHoursCopy } from "../transformers/cafe-card";
import { CafeHoursModel } from "../integrations/orion/models/cafe-hours-model";

const weekdayToMonThruSunIndex = {
  Mon: 0,
  Tue: 1,
  Wed: 2,
  Thu: 3,
  Fri: 4,
  Sat: 5,
  Sun: 6,
} as const;

/**
 * Normally, Sunday is 0, but we want it to be 6. Adding 6 and modding by 7
 * results in everything shifting up by 1 and Sunday being 6.
 * Monday: 1 + 6 -> 7 % 7 = 0
 * Tuesday: 2 + 6 -> 8 % 7 = 1
 * ...
 * Sunday: 0 + 6 -> 6 % 7 = 6
 * @param day
 * @returns
 */
export const getMonThruSunDay = (day: number): number => (day + 6) % 7;

const getTimezone = (cafeHours: CafeHoursModel[]): string | undefined => {
  const timezone = cafeHours[0]?.timezone;

  const inconsistentTimezones = cafeHours.some(
    (cafeHour) => cafeHour?.timezone && cafeHour.timezone !== timezone,
  );

  if (timezone && inconsistentTimezones) {
    throw new Error("Cafe hours contain inconsistent timezones");
  }

  return timezone;
};

const getCafeLocalNow = (
  timezone: string,
): { cafeToday: number; cafeLocalNowTime: number } => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date());

  const weekday = parts.find(({ type }) => type === "weekday")
    ?.value as keyof typeof weekdayToMonThruSunIndex;
  const hour = parts.find(({ type }) => type === "hour")?.value;
  const minute = parts.find(({ type }) => type === "minute")?.value;

  return {
    cafeToday: weekdayToMonThruSunIndex[weekday],
    cafeLocalNowTime: parseFloat(`${hour}.${minute}`),
  };
};

/** Get the hoursPresentation string for a cafe
 *
 * Taking in the cafe's open and close hours in the cafe's local time, we must
 * compare that to the user's local "now" time to determine if the cafe is
 * pre-open, currently open, or post close for the day. We then return a string
 * that displays the next useful event (ie: "Open until <today's close time>" or
 * "Opens <next open day & time>"). We must consider timezone's that result in
 * full day offsets as well as days that cafes are closed for various reasons.
 *
 * @param cafeHours
 * @param copy
 * @returns { string } ie: "Open until 5:00 PM" or "Opens Wed at 7:00 AM"
 */
export const getHoursPresentation = (
  cafeHours: CafeHoursModel[],
  copy: CafeHoursCopy,
): string => {
  const timezone = getTimezone(cafeHours);

  if (!timezone) return undefined;

  // get the cafe's local time and day directly from its timezone
  const { cafeToday, cafeLocalNowTime } = getCafeLocalNow(timezone);

  // check if cafe is open right now
  const todayOpens = CafeHoursModel.getTimeNumber(
    cafeHours?.[cafeToday]?.opens,
    timezone,
  );
  const todayCloses = CafeHoursModel.getTimeNumber(
    cafeHours?.[cafeToday]?.closes,
    timezone,
  );

  const cafeIsOpen: boolean =
    todayOpens !== undefined &&
    todayCloses !== undefined &&
    cafeLocalNowTime >= todayOpens &&
    cafeLocalNowTime < todayCloses;

  // get when the cafe closes today, in the cafe's local time
  const cafeCloseTime = cafeHours?.[cafeToday]?.closes
    ? `${copy.openTil} ${CafeHoursModel.formatTime(
        cafeHours[cafeToday].closes,
        timezone,
      )}`
    : undefined;

  const getNextOpenTime = () => {
    const openDaysHrs = cafeHours?.filter((cH) => cH?.opens);

    const nextOpenDayHrs =
      cafeLocalNowTime < todayOpens
        ? (openDaysHrs?.find((day) => day?.day >= cafeToday) ?? openDaysHrs[0])
        : (openDaysHrs?.find((day) => day?.day > cafeToday) ?? openDaysHrs[0]);

    return nextOpenDayHrs?.opens
      ? `${copy.opens} ${CafeHoursModel.formatWeekday(
          nextOpenDayHrs.opens,
          timezone,
          "en-US",
          "short",
        )} ${CafeHoursModel.formatTime(nextOpenDayHrs.opens, timezone)}`
      : undefined;
  };

  return cafeIsOpen ? cafeCloseTime : getNextOpenTime();
};
